import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

// Importing necessary icons
import {
  ChevronDown,
  Star,
  MapPin,
  Calendar,
  Camera,
  Coffee,
  Wifi,
  Utensils,
} from "lucide-react";

const HeroSection = () => {
  const [scrolled, setScrolled] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const navigate = useNavigate();
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [guests, setGuests] = useState("1");
  const [scrollY, setScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const handleCheckAvailability = () => {
    if (!checkIn || !checkOut) {
        alert("Please select both check-in and check-out dates.");
        return;
    }

    if (checkOut <= checkIn) {
        alert("Check-out date must be after the check-in date.");
        return;
    }

    const params = new URLSearchParams({
        checkIn,
        checkOut,
        guests,
    }).toString();

    navigate(`/room-booking?${params}`);
};


  // Hero images array
  const heroImages = [
    "https://ik.imagekit.io/tdlebsr5e/0N2A1274.JPG?updatedAt=1747388657718",
    "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/FINAL%20EDITED%20GULMOHAR/0N2A6045.JPG?updatedAt=1747392885685",
    "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/PICTURES/PROPERTY%20SHOOT/01/0N2A9967.JPG?updatedAt=1747393100590",
    "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/PICTURES/EVENTS/ROOFTOP%20RECEPTION/WhatsApp%20Image%202025-05-16%20at%2018.06.52.jpeg?updatedAt=1747404487131",
  ];

  useEffect(() => {
    // Import AOS library
    const AOS = require("aos");
    AOS.init({
      duration: 1000,
      once: true,
      easing: "ease-out",
    });

    // Handle scroll event for navbar transparency
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    // Image slideshow interval
    const imageInterval = setInterval(() => {
      setCurrentImage((prev) =>
        prev === heroImages.length - 1 ? 0 : prev + 1
      );
    }, 6000);

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearInterval(imageInterval);
    };
  }, [scrolled, heroImages.length]);

  const formatDate = (date) => {
    return date.toISOString().split("T")[0];
  };

  useEffect(() => {
    const today = new Date();
    const tomorrow = new Date();
    today.setDate(today.getDate() + 1);
    tomorrow.setDate(today.getDate() + 1);

    setCheckIn(formatDate(today));
    setCheckOut(formatDate(tomorrow));
  }, []);

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 1.2 } },
  };

  const slideUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
  };

  const slideRight = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
  };

  const slideLeft = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
  };

  const staggered = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // Tailwind's md: breakpoint
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Navigation items
  const navItems = [
    "Home",
    "Rooms",
    "Dining",
    "Spa & Wellness",
    "Experiences",
    "Gallery",
    "Contact",
  ];

  return (
    <div>
      <div className="relative w-full h-screen overflow-hidden bg-black">
        {/* Background Image Slideshow */}
        <div className="absolute inset-0 w-full h-full">
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70 z-10"></div>

          {/* Image Slideshow */}
          {heroImages.map((image, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0 }}
              animate={{
                opacity: currentImage === index ? 1 : 0,
                scale: currentImage === index ? 1.05 : 1,
              }}
              transition={{
                opacity: { duration: 1.5 },
                scale: { duration: 8 },
              }}
              className="absolute inset-0 overflow-hidden"
              style={{ zIndex: currentImage === index ? 5 : 1 }}
            >
              <div
                className="w-full h-full bg-center bg-cover"
                style={{ backgroundImage: `url('${image}')` }}
              ></div>
            </motion.div>
          ))}

          {/* Hero Content */}
          <div className="relative h-screen flex items-center justify-center px-6 z-20">
            <div className="container mx-auto max-w-6xl">
              <div className="flex flex-col items-center text-center">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  className="mb-4"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <Star
                      className="w-4 h-4 text-amber-500"
                      fill="currentColor"
                    />
                    <Star
                      className="w-4 h-4 text-amber-500"
                      fill="currentColor"
                    />
                    <Star
                      className="w-4 h-4 text-amber-500"
                      fill="currentColor"
                    />
                    <Star
                      className="w-4 h-4 text-amber-500"
                      fill="currentColor"
                    />
                    <Star
                      className="w-4 h-4 text-amber-500"
                      fill="currentColor"
                    />
                  </div>
                </motion.div>

                <motion.h2
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="text-white text-4xl md:text-5xl lg:text-7xl font-serif tracking-wide mb-4"
                >
                  The Gulmohar Grand
                </motion.h2>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="w-24 h-0.5 bg-amber-500 my-6"
                />

                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  className="text-gray-200 text-lg md:text-xl max-w-3xl mb-8 font-light leading-relaxed"
                >
                  Redefining contemporary luxury in Dev Bhoomi Group Companies, Himachal
                  Pradesh. Experience unparalleled hospitality, where every
                  moment is crafted for perfection.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                  className="flex flex-col sm:flex-row gap-4 mt-6"
                >
                  <Link
                    to="/room-booking"
                    className="bg-amber-700 hover:bg-amber-600 text-white px-8 py-4 text-sm uppercase tracking-widest transition-all transform hover:scale-105 flex items-center justify-center gap-2"
                  >
                    <Calendar className="w-4 h-4" />
                    Book Your Stay
                  </Link>

                  <Link
                    to="/gallery"
                    className="border border-white/40 hover:border-white text-white px-8 py-4 text-sm uppercase tracking-widest transition-all backdrop-blur-sm"
                  >
                    Explore Gallery
                  </Link>
                </motion.div>

                {/* <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.2 }}
              className="absolute bottom-10 left-0 right-0 flex justify-center"
            >
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ repeat: Infinity, duration: 2 }}
              >
                <ChevronDown className="text-white/80 w-8 h-8" />
              </motion.div>
            </motion.div> */}
              </div>
            </div>
          </div>

          {/* Image Gallery Indicators */}
          <div className="absolute bottom-32 left-0 right-0 z-20 hidden md:flex justify-center">
            <div className="flex space-x-2">
              {heroImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImage(index)}
                  className={`w-16 h-1 transition-all ${
                    currentImage === index ? "bg-amber-500" : "bg-white/30"
                  }`}
                  aria-label={`View image ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Floating Location Badge */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="absolute bottom-10 left-10 hidden md:flex items-center text-white z-20"
          >
            <MapPin className="w-5 h-5 mr-2 text-amber-500" />
            <span className="text-sm tracking-wider">
              Himachal Pradesh, India
            </span>
          </motion.div>

          {/* Floating CMD Badge */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="absolute bottom-10 right-10 hidden md:flex flex-col items-end text-white z-20"
          >
            <p className="text-xs text-amber-500 uppercase tracking-widest">
              Chairman & Managing Director
            </p>
            <p className="text-sm tracking-wider">Rajinder Vashista</p>
          </motion.div>

          {/* Image Attribution */}
          {/* <div className="absolute top-6 right-6 z-20 hidden lg:block">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="bg-black/20 backdrop-blur-sm px-4 py-2 rounded-full"
        >
          <p className="text-white/80 text-xs">The Gulmohar Grand Experience</p>
        </motion.div>
      </div> */}

          {/* Featured Amenities */}
          {/* <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="absolute bottom-32 left-10 z-20 hidden xl:flex"
      >
        <div className="bg-black/30 backdrop-blur-md p-4 border-l-2 border-amber-500">
          <p className="text-white/80 text-xs uppercase tracking-wider mb-4">Premium Amenities</p>
          <div className="flex flex-col space-y-4">
            <div className="flex items-center">
              <Wifi className="w-4 h-4 text-amber-500 mr-3" />
              <span className="text-white text-sm">High-Speed WiFi</span>
            </div>
            <div className="flex items-center">
              <Utensils className="w-4 h-4 text-amber-500 mr-3" />
              <span className="text-white text-sm">Five-Star Dining</span>
            </div>
            <div className="flex items-center">
              <Coffee className="w-4 h-4 text-amber-500 mr-3" />
              <span className="text-white text-sm">Luxury Spa</span>
            </div>
            <div className="flex items-center">
              <Camera className="w-4 h-4 text-amber-500 mr-3" />
              <span className="text-white text-sm">Scenic Views</span>
            </div>
          </div>
        </div>
      </motion.div> */}
        </div>
      </div>

      <div className="font-serif bg-stone-50 text-stone-800 overflow-x-hidden">
        {/* Booking Widget */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.4 }}
          className="absolute -bottom-6 left-0 right-0 hidden lg:block z-30"
        >
          <div className="container mx-auto font-serif">
            <div className="bg-black/50 backdrop-blur-lg border border-white/20 rounded-sm shadow-2xl p-5 max-w-3xl mx-auto flex flex-wrap justify-between items-center">
              <div className="w-full md:w-1/4 p-2">
                <label className="text-white/80 text-xs uppercase tracking-wider mb-1 block">
                  Check-In
                </label>
                <input
                  type="date"
                  value={checkIn}
                  onChange={(e) => {
                    setCheckIn(e.target.value);
                    // Reset check-out if it's before new check-in
                    if (checkOut && e.target.value >= checkOut) {
                      setCheckOut("");
                    }
                  }}
                  className="w-full bg-transparent border-b border-white/30 text-white py-2 focus:outline-none focus:border-amber-500"
                />
              </div>
              <div className="w-full md:w-1/4 p-2">
                <label className="text-white/80 text-xs uppercase tracking-wider mb-1 block">
                  Check-Out
                </label>
                <input
                  type="date"
                  value={checkOut}
                  min={checkIn || undefined} // Prevent selecting before check-in
                  onChange={(e) => setCheckOut(e.target.value)}
                  className="w-full bg-transparent border-b border-white/30 text-white py-2 focus:outline-none focus:border-amber-500"
                />
              </div>

              <div className="w-full md:w-1/4 p-2">
                <label className="text-white/80 text-xs uppercase tracking-wider mb-1 block">
                  Guests
                </label>
                <select
                  value={guests}
                  onChange={(e) => setGuests(e.target.value)}
                  className="w-full bg-transparent border-b border-white/30 text-white py-2 focus:outline-none focus:border-amber-500"
                >
                  <option className="bg-gray-900" value="1">
                    1 Guest
                  </option>
                  <option className="bg-gray-900" value="2">
                    2 Guests
                  </option>
                  <option className="bg-gray-900" value="3">
                    3 Guests
                  </option>
                  <option className="bg-gray-900" value="4">
                    4 Guests
                  </option>
                </select>
              </div>
              <div className="w-full md:w-1/4 p-2">
                <button
                  onClick={handleCheckAvailability}
                  className="w-full bg-amber-700 hover:bg-amber-600 text-white py-3 uppercase tracking-widest text-sm transition-all transform hover:scale-105"
                >
                  Check Availability
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        {/* <section className="py-24 md:py-32 bg-stone-900 text-white px-6 md:px-16 lg:px-24 relative overflow-hidden">
                    <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80')] bg-cover bg-center opacity-20"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-stone-900 via-stone-900/80 to-transparent"></div>
    
                    <div className="max-w-7xl mx-auto relative z-10">
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-100px" }}
                            variants={fadeIn}
                            className="md:max-w-xl"
                        >
                            <span className="inline-block px-4 py-1 border border-amber-300 text-amber-300 text-xs tracking-widest uppercase mb-6">
                                Exclusive Experience
                            </span>
                            <h3 className="text-3xl md:text-5xl font-light mb-6 leading-tight tracking-wide">
                                EXPERIENCE THE <br /><span className="text-amber-400">Y-GEN LIFESTYLE</span>
                            </h3>
                            <div className="w-24 h-1 bg-amber-500 mb-8"></div>
                            <p className="text-stone-300 text-lg mb-12 leading-relaxed">
                                Whether you're visiting for business or leisure, The Gulmohar Grand Hotel promises an unforgettable stay that redefines conventional hospitality. Book now and immerse yourself in luxury.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <button className="px-10 py-4 bg-amber-500 text-white hover:bg-amber-600 transition duration-300 tracking-widest text-sm uppercase">
                                    Book Your Stay
                                </button>
                                <button className="px-10 py-4 border border-white text-white hover:bg-white hover:text-stone-900 transition duration-300 tracking-widest text-sm uppercase">
                                    Explore Packages
                                </button>
                            </div>
                        </motion.div>
                    </div>
                </section> */}
      </div>
    </div>
  );
};

export default HeroSection;
